import { subtitle } from '@/components/textData/utils';
import { Company } from '@/modules/company/entities/interfaces/company';
import {
  UsageStatement,
  UsageStatementSearchParams,
} from '@/modules/copay/entities/interfaces/usageStatements';
import { CopayService } from '@/modules/copay/services/copayService';
import useFileDownload from '@/modules/files/hooks/useFileDownload/useFileDownload';
import { useRequestPaginatedList } from '@/shared/hooks/useRequestData/useRequestPaginatedList';
import ICONS from '@/shared/entities/constants/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Chip, Icon, Loader, Select } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

interface DownloadUsageStatementFileProps {
  company?: Company;
  companyParentId?: string;
  callbackDownload?: (usageStatement: UsageStatement) => void;
}

export const DownloadUsageStatementFile: React.FC<DownloadUsageStatementFileProps> = ({
  company,
  companyParentId,
  callbackDownload,
}) => {
  const currentYear = new Date().getFullYear();
  const initialYear = 2023;

  const [selectedCompetenceMonth, setSelectedCompetenceMonth] = useState(new Date().getMonth() + 1);
  const [selectedCompetenceYear, setSelectedCompetenceYear] = useState(new Date().getFullYear());
  const { downloadFile, loading: loadingDownload } = useFileDownload();

  const { data, loading, loadData } = useRequestPaginatedList({
    autoLoad: true,
    source: (filters: Partial<UsageStatementSearchParams>) =>
      new CopayService().getUsageStatements({
        ...filters,
        companyGroupId: company?.companyGroupId,
      }),
  });

  const { data: mostRecentUsageStatements, loading: loadingMostRecentUsageStatements } =
    useRequestPaginatedList({
      autoLoad: true,
      source: () =>
        new CopayService().getUsageStatements({
          companyGroupId: company?.companyGroupId,
          pageSize: 1,
          sort: ['-competenceYear', '-competenceMonth'],
        }),
    });

  const { validMonths, usageStatementIdMonth } = useMemo(() => {
    const validMonths: number[] = [];
    const usageStatementIdMonth: Record<number, UsageStatement> = {};

    data.forEach((usageStatement) => {
      if (!validMonths.includes(usageStatement.competenceMonth)) {
        validMonths.push(usageStatement.competenceMonth);
        usageStatementIdMonth[usageStatement.competenceMonth] = usageStatement;
      }
    });

    return {
      validMonths,
      usageStatementIdMonth,
    };
  }, [data]);

  const optionSelectMonth = (option: {
    value: string | number;
    text: string;
    isSelected: boolean;
  }) => {
    const monthNumber = Number(option.value);
    const [mostRecentUsageStatement] = mostRecentUsageStatements;

    return (
      <div
        style={{ opacity: validMonths.includes(monthNumber) ? '1' : '.5', width: '100%' }}
        key={monthNumber}>
        <Grid alignContent='justify' alignItems='center' stretch>
          {option.text}
          {monthNumber === mostRecentUsageStatement?.competenceMonth &&
            selectedCompetenceYear === mostRecentUsageStatement?.competenceYear && (
              <Chip
                rounded
                size='small'
                label='Arquivo mais recente'
                invertColor
                color='colorDefault'
              />
            )}
        </Grid>
      </div>
    );
  };

  const download = async () => {
    const success = await downloadFile(
      usageStatementIdMonth[selectedCompetenceMonth].originalFileId,
      companyParentId,
    );

    if (success) {
      callbackDownload?.(usageStatementIdMonth[selectedCompetenceMonth]);
    }
  };

  useEffect(() => {
    if (mostRecentUsageStatements.length === 0) {
      return;
    }
    const [mostRecentUsageStatement] = mostRecentUsageStatements;
    setSelectedCompetenceMonth(mostRecentUsageStatement.competenceMonth);
    setSelectedCompetenceYear(mostRecentUsageStatement.competenceYear);
  }, [mostRecentUsageStatements]);

  return (
    <Grid column gap={16} stretch alignItems='center' alignContent='center'>
      <Grid stretch gap={16}>
        <Select
          label='Mês'
          fixedList
          loading={loading || loadingMostRecentUsageStatements}
          value={String(selectedCompetenceMonth)}
          onChange={(year: string) => setSelectedCompetenceMonth(Number(year))}
          customOption={(option, isSelected) =>
            optionSelectMonth({
              value: option.value,
              text: option.text,
              isSelected,
            })
          }
          data={Array.from({ length: 12 }).map((_, index) => {
            const value = String(index + 1);
            return {
              value,
              text: subtitle.month[value],
            };
          })}
        />

        <Select
          label='Ano'
          fixedList
          value={String(selectedCompetenceYear)}
          onChange={(year: string) => {
            setSelectedCompetenceYear(Number(year));
            loadData(company?.id, {
              competenceYear: Number(year),
            });
          }}
          data={Array.from({ length: currentYear - initialYear + 1 }).map((_, index) => {
            const value = String(initialYear + index);
            return {
              value,
              text: value,
            };
          })}
        />
      </Grid>

      <Grid alignContent='center'>
        <Button
          testId='download-usage-statement-file-button'
          onClick={download}
          color='colorDefault'
          disabled={!usageStatementIdMonth[selectedCompetenceMonth]}>
          <Grid padding={12} alignItems='center' alignContent='center' gap={8}>
            {loadingDownload ? <Loader /> : <Icon name={ICONS.DOWNLOAD} />}
            Baixar arquivo de isenção
          </Grid>
        </Button>
      </Grid>
    </Grid>
  );
};
